import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Target, Heart, Users, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';

const ICON_MAP = { Sparkles, Target, Heart, Users };

export default function AboutPage() {
  const [selectedMember, setSelectedMember] = useState(null);
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        // Try API first
        const res = await fetch(`${API_URL}/api/about`);
        if (res.ok) {
          const data = await res.json();
          setAboutData(data);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('API fetch failed, trying local fallback:', err);
      }
      // Fallback to local JSON file
      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/cms_data/about.json`);
        if (res.ok) {
          const data = await res.json();
          setAboutData(data);
        }
      } catch (err) {
        console.error('Local fallback also failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-accent-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  const teamMembers = aboutData?.teamMembers || [];
  const values = (aboutData?.values || []).map(v => ({
    ...v,
    icon: ICON_MAP[v.icon] || Sparkles
  }));
  const cutArounds = aboutData?.cutArounds || [];
  const getCutAround = (id) => cutArounds.find(c => c.id === id) || {};

  return (
    <div data-testid="about-page" className="min-h-screen bg-brand-dark">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background glows */}
        <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-brand-primary/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-accent-gold/10 rounded-full blur-[120px]" />
        
        <div className="max-w-6xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="font-accent text-accent-gold/80 text-sm tracking-[0.4em] mb-6 uppercase"
            >
              About Us
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight"
            >
              {aboutData?.heroTitle || 'Creating Magical Family Live Shows & STEM Experiences'}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="font-body text-white/70 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed mb-8"
            >
              {aboutData?.heroSubtitle || 'Launched in 2013 by Mark Thompson, we\'re a one-stop destination for Theatre and Events markets.'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link
                to="/shows"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg transition-all duration-500 hover:scale-105"
                style={{ backgroundColor: '#D4AF37', color: '#020120' }}
              >
                See Our Shows
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/history"
                className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-500 hover:bg-white/10"
              >
                Our Story
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="pt-20 lg:pt-28 pb-0 relative">
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-4 uppercase">
              What We Do
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8">
              We Believe in <span className="text-accent-gold">Entertaining</span><br />
              & <span className="text-accent-gold">Educating</span>
            </h2>
            <p className="font-body text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
              {aboutData?.missionText || 'We specialise in working with well-known children\'s brands and educational institutions in the STEM fields to create attractive, engaging live experiences which entertain, educate and create a pathway to a life of exploration.'}
            </p>
          </motion.div>

          {/* Values Grid + Aladdin cut-around side by side */}
          <div className="grid lg:grid-cols-[1fr_450px] gap-8 items-end">
            <div className="grid sm:grid-cols-2 gap-6 pb-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-accent-gold/20 flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-accent-gold" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-white/60 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
            {/* Cut-around - fills full height, flush at bottom */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex items-end justify-center h-full overflow-hidden"
            >
              <img
                src={`${process.env.PUBLIC_URL}${getCutAround('about-values').image || '/cutarounds/fireman-sam-circus.png'}`}
                alt={getCutAround('about-values').alt || 'Characters'}
                className="w-[200%] max-w-none drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)] block"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section - contrasting bg for cut-around overlap effect */}
      <section className="py-16 bg-[#0a0a3a] border-y border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { number: "20+", label: "Years of Experience" },
              { number: "1M+", label: "Tickets Sold" },
              { number: "20+", label: "Shows Produced" },
              { number: "15+", label: "Countries Toured" }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="font-display text-4xl lg:text-5xl font-bold text-accent-gold mb-2">
                  {stat.number}
                </div>
                <div className="font-body text-white/60 text-sm">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Character Band - Fireman Sam Circus */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-end"
            >
              <motion.img
                src={`${process.env.PUBLIC_URL}${getCutAround('about-screen-to-stage').image || '/cutarounds/aladdin.png'}`}
                alt={getCutAround('about-screen-to-stage').alt || 'Characters'}
                className="w-96 lg:w-[520px] xl:w-[600px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)] block"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="py-12 lg:py-16"
            >
              <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-4 uppercase">
                Bringing Characters to Life
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">
                From Screen to <span className="text-accent-gold">Stage</span>
              </h2>
              <p className="font-body text-white/70 text-lg leading-relaxed">
                {aboutData?.screenToStageText || 'We transform beloved characters into unforgettable live experiences.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="pt-20 lg:pt-28 pb-20 lg:pb-28 relative bg-[#0a0a3a]">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-[150px]" />
        
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-accent text-accent-gold text-sm tracking-[0.3em] mb-4 uppercase">
              Who's On The Team
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Meet Our <span className="text-accent-gold">Team</span>
            </h2>
            <p className="font-body text-white/70 text-lg max-w-2xl mx-auto">
              Combining years of experience in all things live – our team are some of the most experienced in the business.
            </p>
          </motion.div>

          {/* Team Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-accent-gold/30 transition-all duration-500">
                  {/* Photo */}
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={`${process.env.PUBLIC_URL}${member.image}`}
                      alt={member.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = `${process.env.PUBLIC_URL}/team/placeholder.svg`; }}
                    />
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-display text-lg font-bold text-white mb-1">
                      {member.name}
                    </h3>
                    <p className="font-body text-accent-gold text-sm font-medium">
                      {member.role}
                    </p>
                    {member.bio && (
                      <p className="font-body text-white/50 text-sm leading-relaxed mt-3">
                        {member.bio}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-accent-gold/10 rounded-full blur-[150px]" />
        
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8">
                {aboutData?.ctaTitle || 'Ready to Experience The Magic?'}
              </h2>
              <p className="font-body text-white/60 text-lg mb-10 max-w-2xl">
                {aboutData?.ctaText || 'From spectacular theatre productions to STEM-packed science shows, find your next unforgettable family experience.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start">
                <Link
                  to="/tour-dates"
                  className="group inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full font-bold text-lg transition-all duration-500 hover:scale-105"
                  style={{ backgroundColor: '#D4AF37', color: '#020120' }}
                >
                  Find Tour Dates
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-10 py-5 rounded-full font-semibold text-lg transition-all duration-500 hover:bg-white/10"
                >
                  Get In Touch
                </Link>
              </div>
            </motion.div>
            {/* Milkshake cut-around - full opacity, proper layout element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex items-end justify-center"
            >
              <img
                src={`${process.env.PUBLIC_URL}${getCutAround('about-cta').image || '/cutarounds/milkshake-copy.png'}`}
                alt={getCutAround('about-cta').alt || 'Characters'}
                className="w-96 xl:w-[480px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Bio Modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className="bg-brand-dark border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-brand-dark/95 backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between z-10">
                <div className="flex items-center gap-4">
                  <img
                    src={`${process.env.PUBLIC_URL}${selectedMember.image}`}
                    alt={selectedMember.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-accent-gold/30"
                  />
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">{selectedMember.name}</h3>
                    <p className="font-body text-accent-gold text-sm">{selectedMember.role}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMember(null)}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  data-testid="close-bio-modal"
                >
                  <X className="w-6 h-6 text-white/70" />
                </button>
              </div>
              <div className="p-6">
                {selectedMember.fullBio.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="font-body text-white/70 text-base leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
